import React from 'react';
import { 
  Box, 
  Typography, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Divider,
  Paper,
  Grid,
  Chip
} from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';
import StraightenIcon from '@mui/icons-material/Straighten';
import ScaleIcon from '@mui/icons-material/Scale';
import CategoryIcon from '@mui/icons-material/Category';
import ConstructionIcon from '@mui/icons-material/Construction';
import InfoIcon from '@mui/icons-material/Info';

/**
 * Component to display structured product specifications
 */
const ProductSpecifications = ({ product }) => {
  if (!product) return null;
  
  const hasDimensions = product.dimensions || product.baseDimensions;
  const hasWeight = product.weight || product.baseWeight;
  const hasMaterials = product.materials && product.materials.length > 0;
  const hasFeatures = product.features && product.features.length > 0;
  const hasCareInstructions = product.careInstructions && product.careInstructions.length > 0;
  
  // If no structured data available, don't render the component
  if (!hasDimensions && !hasWeight && !hasMaterials && !hasFeatures && !hasCareInstructions) {
    return null;
  }
  
  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 3, mt: 4, borderRadius: 2 }}>
      <Typography variant="h6" gutterBottom>
        Product Specifications
      </Typography>
      
      <Grid container spacing={3}>
        {/* Dimensions */}
        {hasDimensions && (
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <StraightenIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle1">Dimensions</Typography>
              </Box>
              
              {product.dimensions ? (
                <Typography variant="body2">
                  {product.dimensions.length}" × {product.dimensions.width}" × {product.dimensions.height}"
                  {product.dimensions.unit === 'cm' && ' (cm)'}
                </Typography>
              ) : (
                <Typography variant="body2">
                  {product.baseDimensions.length}" × {product.baseDimensions.width}" × {product.baseDimensions.height}"
                </Typography>
              )}
            </Box>
          </Grid>
        )}
        
        {/* Weight */}
        {hasWeight && (
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ScaleIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle1">Weight</Typography>
              </Box>
              
              {product.weight ? (
                <Typography variant="body2">
                  {product.weight.value} {product.weight.unit}
                </Typography>
              ) : (
                <Typography variant="body2">
                  {product.baseWeight} g
                </Typography>
              )}
            </Box>
          </Grid>
        )}
        
        {/* Capacity */}
        {product.capacity && (
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <InventoryIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle1">Capacity</Typography>
              </Box>
              <Typography variant="body2">{product.capacity}</Typography>
            </Box>
          </Grid>
        )}
        
        {/* Production Time */}
        {product.productionTime && (
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LocalShippingIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle1">Production Time</Typography>
              </Box>
              <Typography variant="body2">{product.productionTime}</Typography>
            </Box>
          </Grid>
        )}
      </Grid>
      
      <Divider sx={{ my: 2 }} />
      
      {/* Materials */}
      {hasMaterials && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <CategoryIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="subtitle1">Materials</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {product.materials.map((material, index) => (
              <Chip 
                key={index} 
                label={material} 
                size="small" 
                variant="outlined" 
                sx={{ mb: 1 }}
              />
            ))}
          </Box>
        </Box>
      )}
      
      {/* Features */}
      {hasFeatures && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <CheckCircleIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="subtitle1">Features</Typography>
          </Box>
          <List dense disablePadding>
            {product.features.map((feature, index) => (
              <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <CircleIcon sx={{ fontSize: 8 }} />
                </ListItemIcon>
                <ListItemText primary={feature} />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
      
      {/* Care Instructions */}
      {hasCareInstructions && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <ConstructionIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="subtitle1">Care Instructions</Typography>
          </Box>
          <List dense disablePadding>
            {product.careInstructions.map((instruction, index) => (
              <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <CircleIcon sx={{ fontSize: 8 }} />
                </ListItemIcon>
                <ListItemText primary={instruction} />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Paper>
  );
};

export default ProductSpecifications;
